// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductLeadTime from "@awc/App/Components/Product/ProductLeadTime";
import { CustomAppState } from "@awc/App/Store/Reducers";
import ProductAddToCartButton from "@awc/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@awc/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@awc/Overrides/Components/ProductUnitOfMeasureSelect";
import useFilteredVariantTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import closeVariantModal from "@insite/client-framework/Store/Components/ProductSelector/Handlers/CloseVariantModal";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import updateVariantSelection from "@insite/client-framework/Store/Components/ProductSelector/Handlers/UpdateVariantSelection";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    skipInventoryValidation?: boolean;
    extendedStyles?: ProductSelectorVariantModalStyles;
    skipProductPrice?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const { variantModalIsOpen, variantModalProductId, variantSelection, selectedVariantProductInfo } =
        getProductSelector(state);
    return {
        variantModalIsOpen,
        variantParentProduct: getProductState(state, variantModalProductId).value,
        variantChildren: getVariantChildrenDataView(state, variantModalProductId).value,
        selectedVariant: getProductState(state, selectedVariantProductInfo?.productId).value,
        selectedVariantProductInfo,
        variantSelection,
        // XNG-Changes
        isQuickOrderPage: getCurrentPage(state).type === "QuickOrderPage",
        isListDetailsPage: getCurrentPage(state).type === "MyListsDetailsPage",
        isOrderHistoryPage: getCurrentPage(state).type === "OrderHistoryPage",
        isMyListsPage: getCurrentPage(state).type === "MyListsPage",
        isMyAccountPage: getCurrentPage(state).type === "MyAccountPage",
        productsInfo: (state as CustomAppState).xVariantProducts.productsInfo,
    };
};

const mapDispatchToProps = {
    closeVariantModal,
    updateVariantSelection,
    setProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ProductSelectorVariantModalStyles {
    modal?: ModalPresentationProps;
    wrapper?: InjectableCss;
    variantSelect?: SelectProps;
    selectButton?: ButtonPresentationProps;
    addToCart?: ButtonPresentationProps;
    productInfoWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
}

export const productSelectorVariantModalStyles: ProductSelectorVariantModalStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContainer: css`
                align-items: flex-start;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    variantSelect: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: 25%;
                text-align: left;
            `,
        },
        cssOverrides: {
            formField: css`
                margin-bottom: 10px;
            `,
            formInputWrapper: css`
                width: 75%;
            `,
        },
    },
    selectButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
};

/**
 * @deprecated Use productSelectorVariantModalStyles instead.
 */
export const quickOrderVariantModalStyles = productSelectorVariantModalStyles;

const ProductSelectorVariantModal = ({
    variantModalIsOpen,
    variantParentProduct,
    variantChildren,
    selectedVariant,
    variantSelection,
    extendedStyles,
    closeVariantModal,
    updateVariantSelection,
    setProduct,
    selectedVariantProductInfo,
    skipInventoryValidation,
    skipProductPrice,
    // XNG-Changes
    isQuickOrderPage,
    isOrderHistoryPage,
    isListDetailsPage,
    history,
    productsInfo,
    isMyListsPage,
    isMyAccountPage,
}: Props) => {
    const [styles] = useState(() => mergeToNew(quickOrderVariantModalStyles, extendedStyles));
    // XNG-Change
    const [qty, setQty] = useState(0);
    const [unitOfMeasure, setUnitOfMeasure] = useState("");

    const isBuyNowLabel =
        !isListDetailsPage && !isQuickOrderPage && !isOrderHistoryPage && !isMyListsPage && !isMyAccountPage;

    if (!variantParentProduct?.variantTraits || !variantChildren) {
        return null;
    }

    const closeModalHandler = () => {
        closeVariantModal();
    };

    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        updateVariantSelection({ traitValueId, variantTraitId });
    };

    const selectButtonClickHandler = () => {
        setProduct({
            productId: variantParentProduct.id,
            variantId: selectedVariant?.id,
            validateProduct: true,
            skipInventoryValidation,
        });
        closeVariantModal();
    };

    const filteredVariantTraits = useFilteredVariantTraits(
        variantParentProduct.variantTraits,
        variantChildren,
        variantSelection,
    );

    // XNG-Changes
    const productInfo = createFromProduct(variantParentProduct);

    const updateQuantity = (qtyOrdered: number) => {
        setQty(qtyOrdered);
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        setUnitOfMeasure(unitOfMeasure);
    };

    const productContext = {
        product: selectedVariant || variantParentProduct,
        productInfo: {
            ...(selectedVariantProductInfo || productsInfo[variantParentProduct.productNumber]),
            qtyOrdered: qty || productInfo.qtyOrdered,
            unitOfMeasure: unitOfMeasure || productInfo.unitOfMeasure,
        },
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    // XNG-Changes
    const quoteClicked = () => {
        if (!selectedVariantProductInfo) {
            return;
        }
        history.push(selectedVariantProductInfo.productDetailPath);
    };

    return (
        <Modal
            {...styles.modal}
            // XNG-Changes
            headline={translate("Select options before adding to order:")}
            isOpen={variantModalIsOpen}
            handleClose={closeModalHandler}
            data-test-selector="modal_ProductSelectorVariantModal"
        >
            {isBuyNowLabel ? (
                <ProductContext.Provider value={productContext}>
                    <StyledWrapper {...styles.productInfoWrapper}>
                        {filteredVariantTraits.map(variantTrait => (
                            <Select
                                labelPosition="top"
                                key={variantTrait.id.toString()}
                                label={`${variantTrait.nameDisplay}:`}
                                value={variantSelection[variantTrait.id]}
                                onChange={event => variantChangeHandler(event.currentTarget.value, variantTrait.id)}
                                data-test-selector={`productSelectorVariantModal_Dropdown_${variantTrait.nameDisplay}`}
                            >
                                <option value="">
                                    {variantTrait.unselectedValue
                                        ? variantTrait.unselectedValue
                                        : `${translate("Select")} ${variantTrait.nameDisplay}`}
                                </option>
                                {variantTrait.traitValues?.map(traitValue => (
                                    <option value={`${traitValue.id}`} key={`${traitValue.id}`}>
                                        {traitValue.valueDisplay}
                                    </option>
                                ))}
                            </Select>
                        ))}
                        <ProductUnitOfMeasureSelect displayPack={true} labelOverride={translate("Length:")} />
                        <StyledWrapper {...styles.qtyWrapper}>
                            <ProductQuantityOrdered showLabel={true} labelOverride={translate("Quantity")} />
                        </StyledWrapper>
                        <ProductPrice showLabel={false} product={productContext} />
                        <ProductLeadTime />
                        {productContext.productInfo.pricing && (
                            <ProductAddToCartButton
                                {...styles.addToCart}
                                data-test-selector="addToCartBtn"
                                closeModalHandler={closeModalHandler}
                            />
                        )}
                    </StyledWrapper>
                </ProductContext.Provider>
            ) : (
                <>
                    <StyledWrapper {...styles.wrapper}>
                        {filteredVariantTraits.map(variantTrait => (
                            <Select
                                {...styles.variantSelect}
                                key={variantTrait.id.toString()}
                                label={variantTrait.nameDisplay}
                                value={variantSelection[variantTrait.id]}
                                onChange={event => variantChangeHandler(event.currentTarget.value, variantTrait.id)}
                                data-test-selector={`productSelectorVariantModal_Dropdown_${variantTrait.nameDisplay}`}
                            >
                                <option value="">
                                    {variantTrait.unselectedValue
                                        ? variantTrait.unselectedValue
                                        : `${translate("Select")} ${variantTrait.nameDisplay}`}
                                </option>
                                {variantTrait.traitValues?.map(traitValue => (
                                    <option value={`${traitValue.id}`} key={`${traitValue.id}`}>
                                        {traitValue.valueDisplay}
                                    </option>
                                ))}
                            </Select>
                        ))}
                    </StyledWrapper>
                    {!skipProductPrice && selectedVariant && selectedVariantProductInfo && (
                        <ProductPrice
                            product={{ product: selectedVariant, productInfo: selectedVariantProductInfo }}
                            showLabel={false}
                        />
                    )}
                    {/* XNG-Changes */}
                    {!selectedVariant && (
                        <Button
                            {...styles.selectButton}
                            disabled={true}
                            data-test-selector="productSelectorVariantModal_selectButton"
                        >
                            {isQuickOrderPage || isMyAccountPage
                                ? translate("Add to Quick Order Form")
                                : translate("Select")}
                        </Button>
                    )}
                    {selectedVariant && selectedVariant.quoteRequired && (
                        <Button {...styles.selectButton} variant="tertiary" onClick={quoteClicked}>
                            {translate("CLICK FOR QUOTE")}
                        </Button>
                    )}
                    {selectedVariant && !selectedVariant.quoteRequired && (
                        <Button
                            {...styles.selectButton}
                            disabled={!selectedVariant || !selectedVariant.id}
                            onClick={selectButtonClickHandler}
                            data-test-selector="productSelectorVariantModal_selectButton"
                        >
                            {/* XNG-Changes */}
                            {isQuickOrderPage || isMyAccountPage
                                ? translate("Add to Quick Order Form")
                                : translate("Select")}
                        </Button>
                    )}
                </>
            )}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductSelectorVariantModal));
